// src/services/bookingService.js

// Use environment variable for API URL, fallback to localhost for development
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export const bookingService = {
  /**
   * Create a new booking
   * @param {Object} bookingData - The booking data payload
   * @returns {Promise} - Returns the API response
   */
  createBooking: async (bookingData) => {
    try {
      console.log('Sending booking data:', bookingData); // For debugging
      
      const response = await fetch(`${API_BASE_URL}/bookings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // Add authorization header if you have authentication
          // 'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(bookingData),
      });
      
      // Parse the response
      const data = await response.json();
      
      if (!response.ok) {
        // Throw error with message from server or default message
        throw new Error(data.message || data.error || 'Booking failed');
      }
      
      return data;
    } catch (error) {
      console.error('Booking API Error:', error);
      throw error;
    }
  },

  /**
   * Get booking by ID
   * @param {string|number} bookingId - The booking ID
   * @returns {Promise} - Returns the booking details
   */
  getBooking: async (bookingId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/bookings/${bookingId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch booking');
      }
      
      return data;
    } catch (error) {
      console.error('Get Booking Error:', error);
      throw error;
    }
  },

  /**
   * Get all bookings (with optional filters)
   * @param {Object} filters - Query filters (date, status, etc.)
   * @returns {Promise} - Returns list of bookings
   */
  getBookings: async (filters = {}) => {
    try {
      // Convert filters object to query string
      const queryString = new URLSearchParams(filters).toString();
      const url = `${API_BASE_URL}/bookings${queryString ? `?${queryString}` : ''}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch bookings');
      }
      
      return data;
    } catch (error) {
      console.error('Get Bookings Error:', error);
      throw error;
    }
  },

  /**
   * Update booking status
   * @param {string|number} bookingId - The booking ID
   * @param {string} status - New status (confirmed, cancelled, completed)
   * @returns {Promise} - Returns updated booking
   */
  updateBookingStatus: async (bookingId, status) => {
    try {
      const response = await fetch(`${API_BASE_URL}/bookings/${bookingId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to update booking');
      }
      
      return data;
    } catch (error) {
      console.error('Update Booking Error:', error);
      throw error;
    }
  },

  /**
   * Cancel booking
   * @param {string|number} bookingId - The booking ID
   * @returns {Promise} - Returns cancellation confirmation
   */
  cancelBooking: async (bookingId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/bookings/${bookingId}/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to cancel booking');
      }
      
      return data;
    } catch (error) {
      console.error('Cancel Booking Error:', error);
      throw error;
    }
  },

  /**
   * Calculate fare estimate
   * @param {Object} tripDetails - Trip details for estimation
   * @returns {Promise} - Returns fare estimate
   */
  calculateFare: async (tripDetails) => {
    try {
      const response = await fetch(`${API_BASE_URL}/bookings/calculate-fare`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(tripDetails),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to calculate fare');
      }
      
      return data;
    } catch (error) {
      console.error('Calculate Fare Error:', error);
      throw error;
    }
  },

  /**
   * Check availability for a vehicle
   * @param {Object} availabilityData - Date, time, vehicle type
   * @returns {Promise} - Returns availability status
   */
  checkAvailability: async (availabilityData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/bookings/check-availability`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(availabilityData),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to check availability');
      }
      
      return data;
    } catch (error) {
      console.error('Check Availability Error:', error);
      throw error;
    }
  }
};

// Optional: Export a default instance
export default bookingService;